
import NotificationActions from 'actions/Notification'
import ActionStatus from 'components/ActionStatus'
import { listOfRoles } from 'components/WithRoleAuthorization/WithRoleAuthorization'
import { ROUTES_NAMES } from 'constants/name.routes'
import { notificationStatus } from 'constants/notificationStatus'
import { toBoolean } from 'helper/string'
import { useEffect, useMemo, useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { MdOutlineModeEditOutline } from 'react-icons/md'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Dispatch } from 'redux'
import { RootReducerState } from 'types/global'
import { NotificationAction, NotificationReducer } from 'types/notification'
import { DAMAGE_LEVELS } from 'utils/damage'
import ActionTable from './Children/ActionTable'
import {
    listOfQuestions,
    TreatmentsStep4Props,
    convertNotPerformToBatchRequest,
    convertNotPerformToUpdateBatchRequest,
} from './helpers'
import { checkboxStyle, EditButton, Title } from './styles'

export const canEditActionPlan = ({
    loggedInUserIsResponsible,
    isSameLocation,
    isSameSector,
    notificationStatusState
}: {
    loggedInUserIsResponsible: boolean
    isSameLocation: boolean
    isSameSector: boolean
    notificationStatusState: string
}) => {
    if (notificationStatusState === notificationStatus[3].label) {
        return [
            listOfRoles.admin,
            listOfRoles.location_qa,
            listOfRoles.corporate_quality_board
        ]
    } else {
        return [
            ...(loggedInUserIsResponsible ? [listOfRoles.admin] : []),
            ...(isSameLocation && isSameSector && loggedInUserIsResponsible
                ? [listOfRoles.location_manager]
                : []),
            ...(isSameLocation && loggedInUserIsResponsible
                ? [listOfRoles.location_qa]
                : [])
        ]
    }
}

const TreatmentsStep4 = ({
    id,
    tooltipAlert,
    currentUser,
    statusIsArchivedOrFinished,
    notificationById,
    notificationStatusState
}: TreatmentsStep4Props) => {
    const navigate = useNavigate()
    const [disabled, setDisabled] = useState(false);
    const [notAllowed, setNotAllowed] = useState<boolean>(false)

    const notificationSelector: NotificationReducer = useSelector((state: RootReducerState) => state.notificationsReducer)
    const notificationDispatch: Dispatch<NotificationAction> = useDispatch()

    useEffect(() => {
        console.log("Loading List Of Action Plan...")
        NotificationActions.getListOfActionPlan(notificationDispatch, id)
    }, [currentUser])

    useEffect(() => {
        if (!notAllowed) {
            setDisabled(toBoolean(notificationSelector?.actionPlans?.[0]?.u_do_not_perform_optional))
        }
    }, [notAllowed, notificationSelector?.actionPlans])

    const hideOrShowCheckUnrealizedOptional = useMemo(() => {
        const imageRiskValue = notificationById?.u_image_risk?.value
        const isImageRisk = imageRiskValue === 'true' || imageRiskValue === 'u_image_risk_yes'
        const damageValue = notificationSelector?.notificationById?.damage_level?.value;
        const hasNeverEvents = notificationSelector?.notificationById?.u_never_events_or_sentinel_event?.value == 'yes'

        const isOlder = notificationSelector?.notificationById?.u_probability_occurence?.value === ''
        if (isOlder) {
            const optionalActionPlanDamages = DAMAGE_LEVELS.filter(({ value }) => value === 'near-miss' || value === 'notifiable')
            return optionalActionPlanDamages.find(item => item.id === damageValue) && !isImageRisk
        }

        const stateActionPlan = notificationSelector?.notificationProcessRequiredStepsRules?.actionPlanMandatory
        return !hasNeverEvents && stateActionPlan === "Optional"
    }, [currentUser])

    const isSameLocation = useMemo(() => {
        return currentUser?.locations && currentUser?.locations?.filter((i) => i.sys_id === notificationById?.location_incident?.value).length > 0
    }, [currentUser, notificationById])

    const isSameSector = useMemo(() => {
        return currentUser?.locations && currentUser?.locations?.filter((i) => i.sys_id === notificationById?.location_incident?.value)[0]?.sectors.filter((i) => i.sys_id === notificationById?.incident_sector?.value).length > 0
    }, [currentUser, notificationById])
    const loggedInUserIsResponsible = useMemo(() => {
        return currentUser?.sys_id && currentUser?.sys_id ===
            notificationById?.u_assigned_to?.value
    }, [currentUser, notificationById])

    useEffect(() => {
        if (canEditActionPlan({ loggedInUserIsResponsible, isSameLocation, isSameSector, notificationStatusState })
            .includes(currentUser?.profile) && !statusIsArchivedOrFinished) {
            setNotAllowed(false)
        } else {
            setNotAllowed(true)
        }
    }, [loggedInUserIsResponsible, isSameLocation, isSameSector, notificationStatusState, statusIsArchivedOrFinished])

    const redirectToPlanForm = () => {
        navigate(`${ROUTES_NAMES.notifications}/action_plan/${id}`)
    }
    const {
        trigger,
        register,
        control,
        formState: { errors },
        getValues,
        reset,
        setValue,
    } = useForm({
        defaultValues: {
            fields: [

            ]
        }
    })
    const { fields } = useFieldArray({
        control,
        name: 'fields'
    })

    const isNotListOfPlaceHolder = () => {
        if (fields.length && fields.length <= 1 && !fields[0].who) {
            return false
        }
        return true
    }
    useEffect(() => {
        if (notificationSelector?.actionPlans?.length) {
            const getFieldsData = notificationSelector?.actionPlans?.map(
                (plan, index) => {
                    const items = listOfQuestions.map((question) => {
                        return {
                            [`${question}`]: plan[question].toString()
                        }
                    })
                    return Object.assign(
                        {
                            sys_id: plan.sys_id,
                            id: plan.sys_id
                        },
                        ...items
                    )
                }
            )
            reset({
                fields: getFieldsData
            })
        } else if (notificationSelector?.actionPlans?.length === 0) {
            reset({
                fields: [

                ]
            })
        }
    }, [notificationSelector?.actionPlans])

    const onChangeCheckedNotPerform = async (checkedValue: boolean) => {
        try {
            if (!currentUser) {
                console.error('Usuário não encontrado')
                return
            }

            // Verificar se há planos existentes para decidir entre CREATE ou UPDATE
            const currentActionPlans = notificationSelector?.actionPlans || []

            if (currentActionPlans.length > 0) {
                // Se há planos existentes, usar UPDATE batch para alterar u_do_not_perform_optional
                const updateBatchRequest = convertNotPerformToUpdateBatchRequest(
                    currentActionPlans,
                    currentUser,
                    checkedValue
                )

                await NotificationActions.updateActionPlanBatch(notificationDispatch, updateBatchRequest)
            } else {
                // Se não há planos existentes, usar CREATE batch
                const createBatchRequest = convertNotPerformToBatchRequest(
                    id,
                    currentUser,
                    checkedValue
                )

                await NotificationActions.createActionPlanBatch(notificationDispatch, createBatchRequest)
            }

            setDisabled(checkedValue)
            // Recarregar dados
            NotificationActions.getListOfActionPlan(
                notificationDispatch,
                id
            )
        } catch (error) {
            console.error('Erro ao alterar status "não realizar":', error)
        }
    };
    return (
        <div
            data-testid="test-notification-treatment-step4"
            className='min-h-[500px] px-8'>
            <Title>Plano de Ação</Title>
            <div className="flex flex-row gap-12 mt-[10px] mb-1">
                Ao responder as perguntas, transformamos em ações práticas todo
                o tratamento da notificação e criamos um checklist que indica as
                atividades, os prazos e as responsabilidades de todos os
                envolvidos na resolução dos problemas identificados. O seu
                propósito é definir as tarefas de forma visual, simples e ágil.
            </div>
            <div className="flex flex-row gap-12 mt-1 mb-10 text-xs text-darkGray">
                Preenchimento obrigatório para todas as notificações, exceto
                Circunstância Notificável e Near Miss.
            </div>
            <fieldset disabled={notAllowed}>
                <div className="flex flex-row gap-12 mt-6 mb-10">
                    <EditButton
                        data-testid="test-go-to-forum-button"
                        onClick={() => {
                            redirectToPlanForm()
                        }}
                        disabled={disabled || notAllowed}
                        type="button"
                        className={`${disabled || notAllowed ? 'bg-disabled text-textSelect font-normal' : ''}`}
                    >
                        <div className="w-[202px] flex justify-center items-center gap-3">
                            <MdOutlineModeEditOutline size={18} color={`${!disabled && !notAllowed ? 'white' : '#4F4F4F'}`} />
                            <span className="inline-block align-middle">
                                Editar Plano de ação
                            </span>
                        </div>
                    </EditButton>
                    {hideOrShowCheckUnrealizedOptional &&
                        <div data-testid="test-toggle-button" className="mt-2 block min-h-[1.5rem] pl-[1.5rem]">
                            <input
                                onChange={(e) => {
                                    onChangeCheckedNotPerform(e.target.checked)
                                }}
                                checked={disabled}
                                name="u_do_not_perform_optional"
                                className={`${checkboxStyle}  ${notAllowed
                                    ? 'opacity-40'
                                    : 'hover:cursor-pointer'
                                    }`}
                                type="checkbox"
                                id="checkUnrealizedOptional"
                            />
                            <label
                                className={`inline-block pl-[0.15rem]  ${notAllowed
                                    ? 'opacity-40'
                                    : 'hover:cursor-pointer'
                                    }`}
                                htmlFor="checkUnrealizedOptional"
                            >
                                Não realizar o preenchimento
                            </label>
                        </div>
                    }
                </div>
            </fieldset>
            {tooltipAlert && (
                <div data-testid="test-tooltip-alert-3">
                    <ActionStatus text={tooltipAlert} status={1} />
                </div>
            )}
            {
                !disabled && fields.length && isNotListOfPlaceHolder() ?
                    <ActionTable
                        trigger={trigger}
                        fields={fields}
                        notAllowed={true}
                        errors={errors}
                        setFocusedInputData={() => { }}
                        register={register}
                        setValue={setValue}
                        control={control}
                        removeField={() => { }}
                        getValues={getValues}
                        isStaticData={true}
                    /> : null
            }
        </div>
    )
}

export { TreatmentsStep4 as ActionPlan }

